import type { ReactNode } from 'react';

/**
 * The shell's icon set: hand-drawn 24-grid strokes, `currentColor`, `aria-hidden`.
 *
 * Hand-drawn rather than an icon package for the same reason there is no component library
 * (non-negotiable 4): an icon set is a design language, and Material's is the one this project is
 * explicitly not using. Stroke weight 1.8 matches the player's icons, which were drawn first — the
 * two sets have to look like one app.
 *
 * The player's own icons stay in `PlayerChrome.tsx`: they are media semantics (play, mute, skip,
 * fullscreen) rather than navigation, and nothing else will ever draw them.
 */

function Icon({
  children,
  size = 20,
  className,
}: {
  readonly children: ReactNode;
  readonly size?: number;
  /** For a caller whose stylesheet sizes the glyph in `em` — see {@link ThumbUpIcon}. */
  readonly className?: string;
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={className}
    >
      {children}
    </svg>
  );
}

/** Popular is a star — acclaim, where Trending's rising line is a measurement. */
export function PopularIcon() {
  return (
    <Icon>
      <path d="m12 3.8 2.5 5.1 5.6.8-4 4 .9 5.6-5-2.7-5 2.7.9-5.6-4-4 5.6-.8z" />
    </Icon>
  );
}

export function SubscriptionsIcon() {
  return (
    <Icon>
      <path d="M6 4.5h12M4.5 8h15" />
      <path d="M4.5 11.5h15v8h-15z" />
      <path d="m10.8 13.8 4 1.7-4 1.7z" fill="currentColor" stroke="none" />
    </Icon>
  );
}

export function PlaylistsIcon() {
  return (
    <Icon>
      <path d="M4 6.5h12M4 11h12M4 15.5h7" />
      <path d="m17 12.8 4 2.4-4 2.4z" fill="currentColor" stroke="none" />
    </Icon>
  );
}

export function HistoryIcon() {
  return (
    <Icon>
      <path d="M3.6 9.2A9 9 0 1 1 3 12" />
      <path d="M3.2 4.8v4.6h4.6" />
      <path d="M12 7.8V12l3.4 2" />
    </Icon>
  );
}

export function SearchIcon() {
  return (
    <Icon>
      <circle cx="10.5" cy="10.5" r="6" />
      <path d="m15 15 4.5 4.5" />
    </Icon>
  );
}

/**
 * Settings, as **sliders** rather than a gear — and that is a correction a browser made.
 *
 * The gear was drawn first: a hub with eight radial teeth, which is the conventional glyph. Next to the
 * theme toggle it was indistinguishable from that button's own sun — same hub, same eight spokes, same
 * 20 px — so the header read as two suns. Sliders share no silhouette with anything else in the set, and
 * they say "adjust" rather than "machinery", which is what the page is.
 */
export function SettingsIcon() {
  return (
    <Icon>
      <path d="M4 7h10M18 7h2M4 12h3M11 12h9M4 17h9M17 17h3" />
      <circle cx="16" cy="7" r="2" />
      <circle cx="9" cy="12" r="2" />
      <circle cx="15" cy="17" r="2" />
    </Icon>
  );
}

export function ThemeIcon({ dark }: { readonly dark: boolean }) {
  return (
    <Icon>
      {dark ? (
        // A crescent, drawn as one path so it stays a stroke at any size.
        <path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5z" />
      ) : (
        <>
          <circle cx="12" cy="12" r="4.2" />
          <path d="M12 2.8v2.4M12 18.8v2.4M2.8 12h2.4M18.8 12h2.4M5.5 5.5l1.7 1.7M16.8 16.8l1.7 1.7M18.5 5.5l-1.7 1.7M7.2 16.8l-1.7 1.7" />
        </>
      )}
    </Icon>
  );
}

/**
 * Where a setting lives, half one: on the account. A cloud rather than a server rack — the reader's
 * mental model is "it follows me", not the machine it sits on. `LockIcon`'s size: it annotates a
 * setting's name rather than being a control.
 */
export function CloudIcon() {
  return (
    <Icon size={14}>
      <path d="M7 17.5h9.75a3.25 3.25 0 0 0 .55-6.45A5.25 5.25 0 0 0 7 9.9a3.8 3.8 0 0 0 0 7.6z" />
    </Icon>
  );
}

/** Where a setting lives, half two: only in this browser. A screen, drawn like the cloud's sibling. */
export function ScreenIcon() {
  return (
    <Icon size={14}>
      <path d="M4 5.5h16v10.5H4z" />
      <path d="M9 19.5h6M12 16.5v3" />
    </Icon>
  );
}

/** The account gate (§13.2). Smaller than the rest: it annotates a label rather than being one. */
export function LockIcon() {
  return (
    <Icon size={14}>
      <path d="M6.5 10.5h11v9h-11z" />
      <path d="M9 10.5V7.5a3 3 0 0 1 6 0v3" />
    </Icon>
  );
}

/**
 * The account affordance in the header. A head and shoulders rather than a lock: the same control is
 * "sign in" and "your session", and the state is carried by the label beside it.
 */
export function AccountIcon() {
  return (
    <Icon size={18}>
      <circle cx="12" cy="9" r="3.2" />
      <path d="M5.5 19.5a6.5 6.5 0 0 1 13 0" />
    </Icon>
  );
}

/**
 * Collecting the other tabs into this one: two page corners behind, and an arrow coming down into
 * the box in front. The arrow points *in* rather than out, because what the control does is bring
 * things here.
 */
export function TabsImportIcon() {
  return (
    <Icon>
      <path d="M9 4.5h10.5V15" />
      <path d="M4.5 8.5h11v11h-11z" />
      <path d="M10 11.5v4.5M7.8 13.8 10 16l2.2-2.2" />
    </Icon>
  );
}

export function MenuIcon() {
  return (
    <Icon>
      <path d="M4 7h16M4 12h16M4 17h16" />
    </Icon>
  );
}

/**
 * A like — the palm as a rounded box on the baseline, the thumb up and over it.
 *
 * Here rather than with a screen for `JumpOutIcon`'s reason: two places count the same thing, and
 * they now have to look like it. A comment's like count and a **video's** like count are
 * the same field under two names (`likeCount` both times), and the watch page was drawing its own
 * thinner thumb at 16 px beside the comments' at 14 — near enough to read as a mistake rather than as
 * two glyphs. `className` exists because one of the two callers sizes it in `em` so it tracks the
 * footer's own type under `[data-scale='tv']`.
 */
export function ThumbUpIcon({ size = 16, className }: { readonly size?: number; readonly className?: string }) {
  return (
    <Icon size={size} className={className}>
      <path d="M3.2 10.4h3.4V21H3.2z" />
      <path d="M6.6 11 11 3.4a2.4 2.4 0 0 1 2.2 3.3L12 10.4h5.9a2.6 2.6 0 0 1 2.5 3.2l-1.2 5.4a2.6 2.6 0 0 1-2.5 2H6.6" />
    </Icon>
  );
}

/**
 * A dislike — {@link ThumbUpIcon} turned a half turn around the glyph's own centre, so the two
 * read as one drawn pair rather than two separate takes on a thumb (Return YouTube Dislike's
 * counter, right of the like one).
 */
export function ThumbDownIcon({ size = 16, className }: { readonly size?: number; readonly className?: string }) {
  return (
    <Icon size={size} className={className}>
      <g transform="rotate(180 12 12)">
        <path d="M3.2 10.4h3.4V21H3.2z" />
        <path d="M6.6 11 11 3.4a2.4 2.4 0 0 1 2.2 3.3L12 10.4h5.9a2.6 2.6 0 0 1 2.5 3.2l-1.2 5.4a2.6 2.6 0 0 1-2.5 2H6.6" />
      </g>
    </Icon>
  );
}

/**
 * A jump out of the app — a plain onward arrow. Here rather than with a screen because two of
 * them say the same act: the watch page's "Watch on" and the channel About tab's "View on".
 * Not the boxed external-link glyph (2026-08-02): that picture promises a new tab, and these
 * links navigate the current one — following a jump-out means "keep watching over there".
 * 18 px: it rides an sm pill's trigger, the Watch later clock's size.
 */
export function JumpOutIcon() {
  return (
    <Icon size={18}>
      <path d="M4.5 12h14.5" />
      <path d="M13.5 6l6 6-6 6" />
    </Icon>
  );
}

/**
 * **YouTube's mark**, in front of a link in a description or a comment that goes to a page of
 * YouTube's this build does not have — its account settings, a community post, a path the URL scheme
 * cannot read. YouTube marks its own links the same way, and a reader who sees it knows before
 * pressing that this one leaves the app.
 *
 * It was drawn here as an outlined badge until the other eight sites got marks (2026-09-04), which
 * made it the odd one in a row of solid glyphs: held against Discord's in the same paragraph, the
 * outline reads a weight lighter and looks like a different kind of thing rather than the same kind
 * pointing somewhere else. So it is the site's own mark now, from the same Simple Icons set as its
 * eight neighbours, and it takes the muted colour from `currentColor` exactly as they do. The
 * triangle is a hole in the plate rather than a shape on top of it, so nothing here has to know the
 * colour of the paragraph behind it.
 *
 * Its pair is {@link VisiousMarkIcon}, which is the accent: solid against solid, and the colour is
 * what says which of the two stays in the app.
 */
export function YouTubeMarkIcon({ className }: { readonly className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false" className={className}>
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    </svg>
  );
}

/**
 * **Visious's own mark** — the favicon's chevron on its plate, in front of a link that opens *here*:
 * a video, a channel, a playlist, a search. The pair is the whole statement, and neither half means
 * anything without the other: one mark says "this stays in the app", the other says "this leaves it".
 *
 * Solid, and the same drawing as `apps/web/public/favicon.svg` down to the chevron's proportions — a
 * mark has one job, which is to be recognised, and the app already has one. The knockout is
 * `on-accent` because the plate is `accent`, so nothing here depends on the colour of the text behind
 * it (the stylesheet supplies both).
 */
export function VisiousMarkIcon({ className }: { readonly className?: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" className={className}>
      <rect x="1.5" y="1.5" width="21" height="21" rx="5.2" fill="currentColor" />
      <path
        d="M7.4 8.2 12 16.4l4.6-8.2"
        fill="none"
        stroke="var(--vs-color-on-accent)"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * **The nine sites a description sends readers to** — the mark in front of a link that leaves for
 * one of them, beside the shortened address `links/social.ts` derives (`twitch.tv/somebody` reads as
 * `somebody`, and the mark is what still says Twitch).
 *
 * Each is the site's own mark, because a mark's only job is to be recognised and a house-drawn
 * approximation of a logo is a worse version of one everybody already knows. The outlines are Simple
 * Icons' (CC0), on the same 24-grid as everything else here, and every one of them is a single filled
 * path in `currentColor` — so they take the muted colour from the stylesheet exactly as YouTube's
 * mark does, and no brand colour enters the paragraph.
 *
 * `XSiteMarkIcon` carries the longer name on purpose: `XMarkIcon` is what half the icon sets in the
 * world call a close button, and a component whose name reads as "the ✕ icon" is one somebody will
 * eventually use as one.
 */

export function TwitchMarkIcon({ className }: { readonly className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false" className={className}>
      <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z" />
    </svg>
  );
}

export function PatreonMarkIcon({ className }: { readonly className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false" className={className}>
      <path d="M22.957 7.21c-.004-3.064-2.391-5.576-5.191-6.482-3.478-1.125-8.064-.962-11.384.604C2.357 3.231 1.093 7.391 1.046 11.54c-.039 3.411.302 12.396 5.369 12.46 3.765.047 4.326-4.804 6.068-7.141 1.24-1.662 2.836-2.132 4.801-2.618 3.376-.836 5.678-3.501 5.673-7.031Z" />
    </svg>
  );
}

export function XSiteMarkIcon({ className }: { readonly className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false" className={className}>
      <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z" />
    </svg>
  );
}

export function FacebookMarkIcon({ className }: { readonly className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false" className={className}>
      <path d="M9.101 23.691v-7.98H6.627v-3.667h2.474v-1.58c0-4.085 1.848-5.978 5.858-5.978.401 0 .955.042 1.468.103a8.68 8.68 0 0 1 1.141.195v3.325a8.623 8.623 0 0 0-.653-.036 26.805 26.805 0 0 0-.733-.009c-.707 0-1.259.096-1.675.309a1.686 1.686 0 0 0-.679.622c-.258.42-.374.995-.374 1.752v1.297h3.919l-.386 2.103-.287 1.564h-3.246v8.245C19.396 23.238 24 18.179 24 12.044c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.628 3.874 10.35 9.101 11.647Z" />
    </svg>
  );
}

export function InstagramMarkIcon({ className }: { readonly className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false" className={className}>
      <path d="M7.0301.084c-1.2768.0602-2.1487.264-2.911.5634-.7888.3075-1.4575.72-2.1228 1.3877-.6652.6677-1.075 1.3368-1.3802 2.127-.2954.7638-.4956 1.6365-.552 2.914-.0564 1.2775-.0689 1.6882-.0626 4.947.0062 3.2586.0206 3.6671.0825 4.9473.061 1.2765.264 2.1482.5635 2.9107.308.7889.72 1.4573 1.388 2.1228.6679.6655 1.3365 1.0743 2.1285 1.38.7632.295 1.6361.4961 2.9134.552 1.2773.056 1.6884.069 4.9462.0627 3.2578-.0062 3.668-.0207 4.9478-.0814 1.28-.0607 2.147-.2652 2.9098-.5633.7889-.3086 1.4578-.72 2.1228-1.3881.665-.6682 1.0745-1.3378 1.3795-2.1284.2957-.7632.4966-1.636.552-2.9124.056-1.2809.0692-1.6898.063-4.948-.0063-3.2583-.021-3.6668-.0817-4.9465-.0607-1.2797-.264-2.1487-.5633-2.9117-.3084-.7889-.72-1.4568-1.3876-2.1228C21.2982 1.33 20.628.9208 19.8378.6165 19.074.321 18.2017.1197 16.9244.0645 15.6471.0093 15.236-.005 11.977.0014 8.718.0076 8.31.0215 7.0301.0839m.1402 21.6932c-1.17-.0509-1.8053-.2453-2.2287-.408-.5606-.216-.96-.4771-1.3819-.895-.422-.4178-.6811-.8186-.9-1.378-.1644-.4234-.3624-1.058-.4171-2.228-.0595-1.2645-.072-1.6442-.079-4.848-.007-3.2037.0053-3.583.0607-4.848.05-1.169.2456-1.805.408-2.2282.216-.5613.4762-.96.895-1.3816.4188-.4217.8184-.6814 1.3783-.9003.423-.1651 1.0575-.3614 2.227-.4171 1.2655-.06 1.6447-.072 4.848-.079 3.2033-.007 3.5835.005 4.8495.0608 1.169.0508 1.8053.2445 2.228.408.5608.216.96.4754 1.3816.895.4217.4194.6816.8176.9005 1.3787.1653.4217.3617 1.056.4169 2.2263.0602 1.2655.0739 1.645.0796 4.848.0058 3.203-.0055 3.5834-.061 4.848-.051 1.17-.245 1.8055-.408 2.2294-.216.5604-.4763.96-.8954 1.3814-.419.4215-.8181.6811-1.3783.9-.4224.1649-1.0577.3617-2.2262.4174-1.2656.0595-1.6448.072-4.8493.079-3.2045.007-3.5825-.006-4.848-.0608M16.953 5.5864A1.44 1.44 0 1 0 18.39 4.144a1.44 1.44 0 0 0-1.437 1.4424M5.8385 12.012c.0067 3.4032 2.7706 6.1557 6.173 6.1493 3.4026-.0065 6.157-2.7701 6.1506-6.1733-.0065-3.4032-2.771-6.1565-6.174-6.1498-3.403.0067-6.156 2.771-6.1496 6.1738M8 12.0077a4 4 0 1 1 4.008 3.9921A3.9996 3.9996 0 0 1 8 12.0077" />
    </svg>
  );
}

export function TikTokMarkIcon({ className }: { readonly className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false" className={className}>
      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
    </svg>
  );
}

export function BlueskyMarkIcon({ className }: { readonly className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false" className={className}>
      <path d="M12 10.8c-1.087-2.114-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.624 6.479.815 2.736 3.713 3.66 6.383 3.364.136-.02.275-.039.415-.056-.138.022-.276.04-.415.056-3.912.58-7.387 2.005-2.83 7.078 5.013 5.19 6.87-1.113 7.823-4.308.953 3.195 2.05 9.271 7.733 4.308 4.267-4.308 1.172-6.498-2.74-7.078a8.741 8.741 0 0 1-.415-.056c.14.017.279.036.415.056 2.67.297 5.568-.628 6.383-3.364.246-.828.624-5.79.624-6.478 0-.69-.139-1.861-.902-2.206-.659-.298-1.664-.62-4.3 1.24C16.046 4.748 13.087 8.687 12 10.8Z" />
    </svg>
  );
}

export function RedditMarkIcon({ className }: { readonly className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false" className={className}>
      <path d="M12 0C5.373 0 0 5.373 0 12c0 3.314 1.343 6.314 3.515 8.485l-2.286 2.286C.775 23.225 1.097 24 1.738 24H12c6.627 0 12-5.373 12-12S18.627 0 12 0Zm4.388 3.199c1.104 0 1.999.895 1.999 1.999 0 1.105-.895 2-1.999 2-.946 0-1.739-.657-1.947-1.539v.002c-1.147.162-2.032 1.15-2.032 2.341v.007c1.776.067 3.4.567 4.686 1.363.473-.363 1.064-.58 1.707-.58 1.547 0 2.802 1.254 2.802 2.802 0 1.117-.655 2.081-1.601 2.531-.088 3.256-3.637 5.876-7.997 5.876-4.361 0-7.905-2.617-7.998-5.87-.954-.447-1.614-1.415-1.614-2.538 0-1.548 1.255-2.802 2.803-2.802.645 0 1.239.218 1.712.585 1.275-.79 2.881-1.291 4.64-1.365v-.01c0-1.663 1.263-3.034 2.88-3.207.188-.911.993-1.595 1.959-1.595Zm-8.085 8.376c-.784 0-1.459.78-1.506 1.797-.047 1.016.64 1.429 1.426 1.429.786 0 1.371-.369 1.418-1.385.047-1.017-.553-1.841-1.338-1.841Zm7.406 0c-.786 0-1.385.824-1.338 1.841.047 1.017.634 1.385 1.418 1.385.785 0 1.473-.413 1.426-1.429-.046-1.017-.721-1.797-1.506-1.797Zm-3.703 4.013c-.974 0-1.907.048-2.77.135-.147.015-.241.168-.183.305.483 1.154 1.622 1.964 2.953 1.964 1.33 0 2.47-.81 2.953-1.964.057-.137-.037-.29-.184-.305-.863-.087-1.795-.135-2.769-.135Z" />
    </svg>
  );
}

export function DiscordMarkIcon({ className }: { readonly className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false" className={className}>
      <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z" />
    </svg>
  );
}
